import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Download, Play, Pause, Volume2, VolumeX, ChevronDown } from "lucide-react";
import ChapterDivider from "../shared/ChapterDivider";
import FooterSignature from "../shared/FooterSignature";
import PremiumCover from "../shared/PremiumCover";
import InteractiveHeart from "../shared/InteractiveHeart";
import useHoverPrefetch from "../shared/useHoverPrefetch";
import ShimmerImg from "../shared/ShimmerImg";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Cinema template — dark, single-column feed with optional background music
 * and a one-tap slideshow button. Each photo gets a full-viewport row that
 * fades and lifts into view as the user scrolls; chapter dividers appear as
 * cinematic interstitials between sections.
 */
export default function CinemaTemplate({ gallery, accent, ui, actions }) {
  const photos = gallery.photos || [];
  const chapters = (gallery.chapters || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const grouped = groupByChapter(photos, chapters);

  return (
    <div className="bg-stone-950 text-stone-100 min-h-screen" style={{ fontFamily: "var(--gallery-font-sans)" }} data-testid="cinema-template">
      <PremiumCover gallery={gallery} accent={accent} selectedCount={ui.selectedCount} />

      {gallery.welcome_message && (
        <div className="max-w-2xl mx-auto px-6 pt-24 pb-6 text-center">
          <p
            className="text-base sm:text-lg leading-relaxed text-stone-300"
            style={{ fontFamily: "var(--gallery-font-serif)" }}
          >
            {gallery.welcome_message}
          </p>
        </div>
      )}

      {ui.phaseBanner}
      {ui.downloadBar}
      {ui.interactive?.headerStrip}

      <MusicAndSlideshowControls gallery={gallery} actions={actions} />

      <div className="pb-20">
        {grouped.map((group, gi) => (
          <section key={group.key}>
            {group.chapter && (
              <ChapterDivider chapter={group.chapter} variant="cinema" index={gi - (grouped[0].chapter ? 0 : 1)} />
            )}
            <div className="space-y-16 sm:space-y-28">
              {group.photos.map((p, idx) => (
                <CinemaPhoto
                  key={p.id}
                  photo={p}
                  actions={actions}
                  ui={ui}
                  watermark={gallery.watermark_enabled}
                  index={idx}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {ui.selectionBar}
      <FooterSignature gallery={gallery} variant="dark" />
    </div>
  );
}

function groupByChapter(photos, chapters) {
  if (!chapters.length) return [{ key: "all", chapter: null, photos }];
  const byId = Object.fromEntries(chapters.map((c) => [c.id, []]));
  const unassigned = [];
  for (const p of photos) {
    if (p.chapter_id && byId[p.chapter_id]) byId[p.chapter_id].push(p);
    else unassigned.push(p);
  }
  const out = [];
  if (unassigned.length) out.push({ key: "intro", chapter: null, photos: unassigned });
  for (const c of chapters) {
    out.push({ key: c.id, chapter: c, photos: byId[c.id] });
  }
  return out.filter((g) => g.photos.length > 0);
}

function CinemaPhoto({ photo, actions, ui, watermark, index }) {
  const fav = ui.favorites?.[photo.id];
  const isFav = !!fav?.favorited;
  const hover = useHoverPrefetch(photo.url);
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1, ease: [0.2, 0.65, 0.3, 0.9] }}
      className="relative group max-w-6xl mx-auto px-4 sm:px-8"
      // Cinema is a single-column full-width layout. Reserve a viewport-sized
      // box so the browser can skip painting off-screen photos and their
      // download starts only as they approach view.
      style={{ contentVisibility: "auto", containIntrinsicSize: "1000px 80vh" }}
      onMouseEnter={hover.onMouseEnter}
      onMouseLeave={hover.onMouseLeave}
      data-testid={`cinema-photo-${photo.id}`}
    >
      <button onClick={() => actions.openLightbox(photo)} className="block w-full">
        <ShimmerImg
          wrapperClassName="w-full max-h-[88vh] overflow-hidden"
          ratio={photo.width && photo.height ? `${photo.width} / ${photo.height}` : "3 / 2"}
          src={`${BACKEND}${photo.url}${photo.url?.includes("?") ? "&" : "?"}${ui.designPreview ? "w=1200&q=82" : "w=1600&q=85"}`}
          alt={photo.filename}
          loading="lazy"
          decoding="async"
          className="w-full h-auto max-h-[88vh] object-contain"
        />
      </button>
      {watermark && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-white/10 text-5xl sm:text-7xl font-light tracking-[0.4em] uppercase rotate-[-15deg]">
          Preview
        </div>
      )}
      <div className="mt-4 flex items-center justify-between text-xs text-stone-500 font-mono px-1">
        <div className="opacity-60 tabular-nums">
          {String(index + 1).padStart(3, "0")} · {photo.filename}
        </div>
        <div className="flex items-center gap-2">
          {ui.interactive?.enabled && (
            <div data-testid={`int-fav-${photo.id}`} className="inline-flex items-center gap-2">
              {ui.interactive.renderCommentBadge?.(photo)}
              <InteractiveHeart
                isMine={ui.interactive.isMine(photo.id)}
                count={ui.interactive.getEntry(photo.id).count}
                collaborators={ui.interactive.getEntry(photo.id).collaborators}
                onToggle={() => ui.interactive.toggle(photo.id)}
                testid={`fav-btn-${photo.id}`}
              />
            </div>
          )}
          {!ui.isDelivered && !ui.interactive?.enabled && (
            <button
              onClick={() => actions.toggleFav(photo.id)}
              className={`inline-flex items-center gap-1.5 px-3 h-8 text-[10px] uppercase tracking-[0.22em] transition-all ${
                isFav ? "text-white" : "bg-white/5 hover:bg-white/15 text-stone-200"
              }`}
              style={isFav ? { backgroundColor: "var(--gallery-accent, #C9A961)" } : undefined}
              aria-label="Favorite"
              data-testid={`fav-btn-${photo.id}`}
            >
              <Heart className={`w-3.5 h-3.5 ${isFav ? "fill-white" : ""}`} />
              {isFav ? "Favorited" : "Favorite"}
            </button>
          )}
          {ui.isDelivered && ui.downloadsAvailable && (
            <button
              onClick={() => actions.downloadOne(photo)}
              className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] uppercase tracking-[0.22em] bg-white/5 hover:bg-white/15 text-stone-200"
              data-testid={`dl-btn-${photo.id}`}
            >
              <Download className="w-3.5 h-3.5" /> Download
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function MusicAndSlideshowControls({ gallery, actions }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const hasMusic = !!gallery.music_url;

  // Try to start music when slideshow_autoplay is on. Browsers require a user
  // gesture for unmuted playback, so we kick off muted and surface a soft
  // unmute pill in the corner.
  useEffect(() => {
    if (!hasMusic) return;
    const el = audioRef.current;
    if (!el) return;
    el.muted = muted;
    if (gallery.slideshow_autoplay) {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, [hasMusic, gallery.slideshow_autoplay, muted]);

  function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }

  return (
    <div className="sticky top-3 z-30 flex justify-center mb-8 px-4">
      <div className="inline-flex items-center gap-1 bg-stone-900/85 border border-white/10 backdrop-blur-md text-stone-100 px-1.5 h-11">
        <button
          onClick={() => actions.startSlideshow()}
          className="inline-flex items-center gap-1.5 px-3 h-9 text-[10px] uppercase tracking-[0.22em] hover:bg-white/10"
          data-testid="cinema-start-slideshow"
        >
          <Play className="w-3 h-3" /> Play slideshow
        </button>
        {hasMusic && (
          <>
            <div className="w-px h-5 bg-white/10" />
            <button
              onClick={toggle}
              className="inline-flex items-center justify-center w-9 h-9 hover:bg-white/10"
              data-testid="cinema-music-toggle"
              aria-label={playing ? "Pause music" : "Play music"}
            >
              {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={() => setMuted((m) => !m)}
              className="inline-flex items-center justify-center w-9 h-9 hover:bg-white/10"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
          </>
        )}
      </div>
      {hasMusic && (
        <audio ref={audioRef} src={`${BACKEND}${gallery.music_url}`} loop preload="auto" />
      )}
    </div>
  );
}
